import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './IndexItem.module.css';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { IndexItem as TIndexItem } from '../../../../../types/library';
import Button from '../../../../../components/Button';
import { DELETE, ADD, ENTER_CONTENT } from '../../../../../constants';
import { limitMaxLen } from '../../../../../utils/util';
import { TooltipWrapper } from '../../../../Tooltip';

interface Index {
  indentYn?: 0 | 1;
  content?: string;
  mappingTable: { [page: number]: number };
  page: number | '';
  start: number;
  max: number;
  min: number;
  idx?: number;
  onEdit: (list: TIndexItem[], idx?: number) => void;
  focusedIdx?: number;
}

export default function IndexItem({
  indentYn = 0,
  content = '',
  mappingTable,
  page,
  start,
  max,
  min,
  idx = 0,
  onEdit,
  focusedIdx = 0,
}: Index) {
  const cx = classNames.bind(styles);

  const [isIndent, setIsIndent] = useState(!!indentYn);
  const [isHighlightedItem, setIsHighlightedItem] = useState(false);
  const [mappedPage, setMappedPage] = useState<number>();

  const { register, setValue, getValues, control, setFocus } = useFormContext();
  const { remove, insert } = useFieldArray({
    name: 'indexList',
  });

  const reversedMappingTable = Object.fromEntries(
    Object.entries(mappingTable).map(([key, value]) => [value, key])
  );

  const toggleIndent = () => {
    setValue(`indexList.${idx}.indentYn`, +!isIndent);
    setIsIndent(!isIndent);
  };

  const removeIndexItem = () => {
    const filteredList = getValues('indexList').filter(
      (item: TIndexItem, i: number) => i !== idx
    );
    remove(idx);
    onEdit(filteredList);
  };

  const addIndexItem = () => {
    const newIdx = idx + 1;
    const curList = [...getValues('indexList')];
    const newIndex: TIndexItem = {
      indentYn: getValues(`indexList.${idx}.indentYn`),
      content: '',
      page: '',
    };

    curList.length ||
      curList.push({
        indentYn: getValues(`indexList.${idx}.indentYn`),
        content: getValues(`indexList.${idx}.content`),
        page: getValues(`indexList.${idx}.page`),
      });
    curList.splice(newIdx, 0, newIndex);
    insert(newIdx, newIndex);
    onEdit(curList, newIdx);
  };

  const highlightItem = () => setIsHighlightedItem(true);

  const removeHighlightItem = () => setIsHighlightedItem(false);

  const moveFocus = (e: React.KeyboardEvent, name: string, idx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setFocus(`indexList.${idx + 1}.${name}`);
    }
  };

  useEffect(() => {
    setValue(`indexList.${idx}.indentYn`, indentYn);
  }, [indentYn]);

  useEffect(() => {
    setValue(`indexList.${idx}.content`, content);
  }, [content]);

  useEffect(() => {
    setValue(`indexList.${idx}.page`, page);
    page !== '' && setMappedPage(mappingTable[page]);
  }, [idx, page, mappingTable]);

  useEffect(() => {
    setFocus(`indexList.${focusedIdx}.content`);
  }, []);

  return (
    <li className={cx('unit_item', { highlighted: isHighlightedItem })}>
      <div className={cx('items', { sub: indentYn })}>
        <div className={cx('items_align')} onClick={toggleIndent}>
          <input
            type="number"
            defaultValue={indentYn}
            hidden
            {...register(`indexList.${idx}.indentYn` as const, {
              valueAsNumber: true,
            })}
          />
          <Button
            name="depth1"
            classnames={[
              'chapter_indent',
              'depth1',
              { on: !getValues(`indexList.${idx}.indentYn`) },
            ]}
            isBlindName={true}
          />
          <Button
            name="depth2"
            classnames={[
              'chapter_indent',
              'depth2',
              { on: getValues(`indexList.${idx}.indentYn`) },
            ]}
            isBlindName={true}
          />
        </div>

        <p>
          <span className={cx('text_clip')}>
            <input
              type="text"
              className={cx('input_t', 'w100')}
              placeholder={ENTER_CONTENT}
              defaultValue={content || ENTER_CONTENT}
              {...register(`indexList.${idx}.content` as const, {
                onChange: ({ target }) => {
                  target.value = limitMaxLen(target.value, 40);
                },
              })}
              onKeyDown={(e) => moveFocus(e, 'content', idx)}
            />
          </span>
          <span className={cx('page')}>
            <Controller
              control={control}
              name={`indexList.${idx}.page`}
              defaultValue={mappedPage || ''}
              render={({ field: { ref } }) => (
                <input
                  type="number"
                  className={cx('input_t', 'w100')}
                  value={mappedPage || ''}
                  placeholder="1"
                  onChange={({ target }) => {
                    target.value = limitMaxLen(target.value, 3, true);
                    setMappedPage(+target.value);

                    const value = target.value
                      ? +target.value < start
                        ? start
                        : +target.value >= max + start
                        ? max + start - 1
                        : +target.value
                      : '';

                    setValue(
                      `indexList.${idx}.page`,
                      reversedMappingTable[value] ?? ''
                    );
                    1;
                  }}
                  ref={ref}
                  onKeyDown={(e) => moveFocus(e, 'page', idx)}
                />
              )}
            />
          </span>
        </p>

        <div className={cx('items_modify')}>
          <TooltipWrapper content={DELETE}>
            {(tooltipProps) => (
              <Button
                name={DELETE}
                classnames={['chapter_delete', 'modify']}
                isBlindName={true}
                handleClick={removeIndexItem}
                handleMouseEnter={highlightItem}
                handleMouseLeave={removeHighlightItem}
                tooltipProps={tooltipProps}
              />
            )}
          </TooltipWrapper>
          <TooltipWrapper content={ADD}>
            {(tooltipProps) => (
              <Button
                name={ADD}
                classnames={['chapter_insert', 'modify']}
                isBlindName={true}
                handleClick={addIndexItem}
                tooltipProps={tooltipProps}
              />
            )}
          </TooltipWrapper>
        </div>
      </div>
    </li>
  );
}
